// Variable Mixer (Jul 5 2026) — DEV-only.
//
// Upload a product, then mix-and-match ONE stable value per family. Pickable
// values come only from the decomposed recipes (Step 1) — nothing invented. The
// render prompt is assembled purely from the recipes' own extracted text.
//
// "Coherent shuffle" = anchor-bundle recombination: pick one recipe as the
// anchor (internally coherent), then swap 1-3 families to OTHER recipes, but
// reject any swap that violates the anchor's negative_constraints. Reshuffles a
// new *coherent* combination on every click — never a random 8-way collision.
//
// DEV-only: /api/dev/mixer/* are auth-skiplist routes; local testing.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE, getAuthHeaders } from '@/lib/api';

interface Opt { recipe_id: string; value: string; text: string; preview_url?: string; }
interface Fam { label: string; type: 'recipe' | 'menu'; options: Opt[]; hint?: string; }
const MENU_SENTINEL = 'gpt';
interface Recipe {
  recipe_id: string; recipe_family: string;
  negative_constraints: string[]; anchor: string; risk_flags: string[];
}
interface Matrix {
  vocab: Record<string, string[]>;
  families: string[];
  pairs: Record<string, { seen: [string, string, number][]; clash: [string, string, string][] }>;
  n_recipes: number;
}
interface FamiliesResp {
  families: Record<string, Fam>;
  order: string[];
  recipes: Recipe[];
  matrix: Matrix | null;
}

function shortId(rid: string): string {
  const [vibe, mb] = (rid || '').split('/');
  const h = (mb || '').replace('moodboard_', '').slice(0, 4);
  return `${vibe}·${h}`;
}

// clash lookup key — families are always ordered so the key is symmetric
function clashKey(fA: string, vA: string, fB: string, vB: string): string {
  return fA < fB ? `${fA}:${vA}|${fB}:${vB}` : `${fB}:${vB}|${fA}:${vA}`;
}
function pick<T>(arr: T[]): T { return arr[Math.floor(Math.random() * arr.length)]; }

export default function VariableMixerPage() {
  const [fams, setFams] = useState<FamiliesResp | null>(null);
  const [sel, setSel] = useState<Record<string, string>>({}); // family -> recipe_id
  const [enabled, setEnabled] = useState<Record<string, boolean>>({}); // family -> on/off lever
  const [anchorId, setAnchorId] = useState<string | null>(null);
  const [prodB64, setProdB64] = useState<string | null>(null);
  const [prodName, setProdName] = useState('');
  const [quality, setQuality] = useState('medium');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [img, setImg] = useState<string | null>(null);
  const [prompt, setPrompt] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/dev/mixer/families`, {
          headers: { ...(await getAuthHeaders()) },
        });
        if (!r.ok) throw new Error(await r.text());
        const data: FamiliesResp = await r.json();
        setFams(data);
        const init: Record<string, string> = {};
        const en: Record<string, boolean> = {};
        for (const f of data.order) {
          const fam = data.families[f];
          if (fam?.type === 'menu') init[f] = MENU_SENTINEL; // GPT picks
          else if (fam?.options.length) init[f] = fam.options[0].recipe_id;
          // backdrop_stage is the alt env+surface variant — default OFF so the
          // proven backdrop-only setup is the default (pick one of the two).
          en[f] = f !== 'backdrop_stage';
        }
        setSel(init);
        setEnabled(en);
      } catch (e) {
        setErr(`families load failed: ${e instanceof Error ? e.message : String(e)}`);
      }
    })();
  }, []);

  const optFor = useCallback((f: string, rid: string): Opt | null =>
    fams?.families[f]?.options.find((o) => o.recipe_id === rid) || null, [fams]);
  const recById = useCallback((rid: string): Recipe | null =>
    fams?.recipes.find((r) => r.recipe_id === rid) || null, [fams]);
  const valOf = useCallback((f: string, rid: string): string =>
    optFor(f, rid)?.value || '', [optFor]);

  // clash set from the explicit compatibility matrix (keys are family-ordered)
  const clashSet = useMemo(() => {
    const s = new Set<string>();
    const pairs = fams?.matrix?.pairs;
    if (!pairs) return s;
    for (const key of Object.keys(pairs)) {
      const [fA, fB] = key.split('|');
      for (const [vA, vB] of pairs[key].clash) s.add(clashKey(fA, vA, fB, vB));
    }
    return s;
  }, [fams]);
  const pairClash = useCallback((f1: string, v1: string, f2: string, v2: string): boolean =>
    !!v1 && !!v2 && f1 !== f2 && clashSet.has(clashKey(f1, v1, f2, v2)), [clashSet]);

  // free shuffle — each style bucket independently draws a random recipe. No
  // matrix gating; the 5 style dimensions blend, and GPT owns placement/props.
  const coherentShuffle = useCallback(() => {
    if (!fams) return;
    const next: Record<string, string> = { ...sel };
    for (const f of fams.order) {
      const fam = fams.families[f];
      if (fam?.type === 'menu') { next[f] = MENU_SENTINEL; continue; } // GPT picks
      const opts = fam?.options || [];
      if (opts.length) next[f] = pick(opts).recipe_id;
    }
    setSel(next);
    setAnchorId(null);
  }, [fams, sel]);

  // live conflict set: a family conflicts if its value clashes (per the matrix)
  // with any other picked family's value.
  const conflicts = useMemo(() => {
    const bad = new Set<string>();
    if (!fams) return bad;
    for (const f of fams.order) {
      const vf = valOf(f, sel[f]); if (!vf) continue;
      for (const g of fams.order) {
        if (g === f) continue;
        if (pairClash(f, vf, g, valOf(g, sel[g]))) { bad.add(f); break; }
      }
    }
    return bad;
  }, [fams, sel, valOf, pairClash]);

  const onFile = useCallback((f: File | null) => {
    if (!f) return;
    const rd = new FileReader();
    rd.onload = () => {
      const dataUrl = String(rd.result || '');
      setProdB64(dataUrl.split(',')[1] || null);
      if (!prodName) setProdName(f.name.replace(/\.[^.]+$/, ''));
    };
    rd.readAsDataURL(f);
  }, [prodName]);

  const render = useCallback(async () => {
    if (!prodB64) { setErr('upload a product image first'); return; }
    // only send levers that are toggled ON — OFF levers are left to the image model
    const activeSel: Record<string, string> = {};
    for (const f of fams?.order || []) if (enabled[f] && sel[f]) activeSel[f] = sel[f];
    if (!Object.keys(activeSel).length) { setErr('turn on at least one lever'); return; }
    setLoading(true); setErr(null); setImg(null); setPrompt(null);
    try {
      const r = await fetch(`${API_BASE}/api/dev/mixer/render`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
        body: JSON.stringify({
          product_image_b64: prodB64, product_name: prodName || undefined,
          selections: activeSel, quality, aspect_ratio: '1:1',
        }),
      });
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();
      setImg(data.image_b64); setPrompt(data.prompt);
    } catch (e) {
      setErr(`render failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally { setLoading(false); }
  }, [prodB64, prodName, sel, quality]);

  const anchorRec = anchorId ? recById(anchorId) : null;

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: 24, color: '#e6e6ea',
                  fontFamily: 'ui-sans-serif, system-ui' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 8 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>Variable Mixer</h1>
        <Link to="/create" style={{ color: '#8ab4ff', fontSize: 13 }}>← back</Link>
      </div>
      <p style={{ color: '#9aa', fontSize: 13, marginBottom: 18 }}>
        7 <b>recipe levers</b> (backdrop · lighting · product-lighting · color · depth · composition ·
        platform) each pulled from one of {fams?.recipes.length ?? '…'} recipes, plus 2 <b>🤖 GPT levers</b>
        (camera angle · props). Toggle any lever OFF to let the model decide it. <b>🎲 Shuffle</b>
        re-draws the recipe levers at random.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '400px 1fr', gap: 28 }}>
        {/* LEFT — controls */}
        <div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: '#9aa', display: 'block', marginBottom: 6 }}>PRODUCT</label>
            <input type="file" accept="image/*" onChange={(e) => onFile(e.target.files?.[0] || null)}
                   style={{ fontSize: 12, color: '#ccc' }} />
            {prodB64 && (
              <div style={{ marginTop: 10 }}>
                <img src={`data:image/png;base64,${prodB64}`} alt="product"
                     style={{ width: 110, height: 110, objectFit: 'contain',
                              background: '#111', borderRadius: 8, border: '1px solid #333' }} />
                <input value={prodName} onChange={(e) => setProdName(e.target.value)} placeholder="product name"
                       style={{ display: 'block', marginTop: 8, width: 280, padding: '6px 8px',
                                background: '#161620', border: '1px solid #333', borderRadius: 6,
                                color: '#e6e6ea', fontSize: 13 }} />
              </div>
            )}
          </div>

          <button onClick={coherentShuffle} disabled={!fams}
                  style={{ width: '100%', padding: '9px', marginBottom: 14, background: '#7c4dff',
                           border: 'none', borderRadius: 6, color: '#fff', fontSize: 13, fontWeight: 600,
                           cursor: fams ? 'pointer' : 'default' }}>
            🎲 Shuffle style
          </button>
          {anchorRec && (
            <div style={{ fontSize: 11, color: '#9aa', marginBottom: 10 }}>
              anchor: <b style={{ color: '#c9b6ff' }}>{shortId(anchorRec.recipe_id)}</b>
              {anchorRec.anchor ? ` — ${anchorRec.anchor}` : ''}
            </div>
          )}

          {fams?.order.map((f) => {
            const fam = fams.families[f];
            const isMenu = fam.type === 'menu';
            const opt = optFor(f, sel[f]);
            const on = enabled[f] !== false;
            return (
              <div key={f} style={{ marginBottom: 12, opacity: on ? 1 : 0.4 }}>
                <label style={{ fontSize: 12, color: isMenu ? '#c9b6ff' : '#9aa', display: 'flex',
                                justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span>{fam.label}
                    {isMenu && <span style={{ fontSize: 9, marginLeft: 6, opacity: 0.7 }}>🤖 GPT picks</span>}
                    {fam.type === 'choice' && <span style={{ fontSize: 9, marginLeft: 6, opacity: 0.7, color: '#7fd' }}>forced</span>}</span>
                  <button onClick={() => setEnabled({ ...enabled, [f]: !on })}
                          style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.4, padding: '2px 8px',
                                   borderRadius: 999, cursor: 'pointer', border: 'none',
                                   background: on ? '#2a6' : '#444', color: on ? '#fff' : '#bbb' }}>
                    {on ? 'ON' : 'OFF'}
                  </button>
                </label>
                {isMenu ? (
                  <div style={{ fontSize: 11, color: on ? '#889' : '#667', lineHeight: 1.4,
                                fontStyle: 'italic' }}>
                    {on ? fam.hint : 'off — model decides freely'}
                  </div>
                ) : (
                  <>
                    <select value={sel[f] || ''} disabled={!on}
                            onChange={(e) => setSel({ ...sel, [f]: e.target.value })}
                            style={{ width: '100%', padding: '7px 8px', background: '#161620',
                                     border: '1px solid #333', borderRadius: 6, color: '#e6e6ea', fontSize: 13 }}>
                      {fam.options.map((o) => (
                        <option key={o.recipe_id} value={o.recipe_id}>
                          {fam.type === 'choice' ? o.value : `${o.value || '(unset)'} · ${shortId(o.recipe_id)}`}
                        </option>
                      ))}
                    </select>
                    {on
                      ? (opt && (opt.text || opt.preview_url) && (
                          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                            {opt.preview_url && (
                              <img src={opt.preview_url} alt="recipe preview"
                                   title="the recipe this lever is pulled from"
                                   style={{ width: 56, height: 56, flexShrink: 0, objectFit: 'cover',
                                            borderRadius: 6, border: '1px solid #333' }} />
                            )}
                            {opt.text && (
                              <div style={{ fontSize: 11, color: '#788', lineHeight: 1.4,
                                            maxHeight: 56, overflow: 'auto' }}>{opt.text}</div>
                            )}
                          </div>
                        ))
                      : (
                          <div style={{ fontSize: 11, color: '#667', marginTop: 4, fontStyle: 'italic' }}>
                            left to the image model
                          </div>
                        )}
                  </>
                )}
              </div>
            );
          })}

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 16 }}>
            <select value={quality} onChange={(e) => setQuality(e.target.value)}
                    style={{ padding: '8px', background: '#161620', border: '1px solid #333',
                             borderRadius: 6, color: '#e6e6ea', fontSize: 13 }}>
              <option value="low">low</option>
              <option value="medium">medium</option>
              <option value="high">high</option>
            </select>
            <button onClick={render} disabled={loading || !prodB64}
                    style={{ flex: 1, padding: '10px', background: loading ? '#333' : '#4a6cf7',
                             border: 'none', borderRadius: 6, color: '#fff', fontSize: 14, fontWeight: 600,
                             cursor: loading || !prodB64 ? 'default' : 'pointer' }}>
              {loading ? 'Rendering…' : 'Render'}
            </button>
          </div>
          {err && <div style={{ color: '#f77', fontSize: 12, marginTop: 10, whiteSpace: 'pre-wrap' }}>{err}</div>}
        </div>

        {/* RIGHT — result */}
        <div>
          {img ? (
            <img src={`data:image/png;base64,${img}`} alt="render"
                 style={{ width: '100%', maxWidth: 640, borderRadius: 10, border: '1px solid #333' }} />
          ) : (
            <div style={{ width: '100%', maxWidth: 640, aspectRatio: '1', borderRadius: 10,
                          border: '1px dashed #333', display: 'flex', alignItems: 'center',
                          justifyContent: 'center', color: '#556', fontSize: 13 }}>
              {loading ? 'rendering…' : 'result appears here'}
            </div>
          )}
          {prompt && (
            <details open style={{ marginTop: 14, fontSize: 11, color: '#9aa' }}>
              <summary style={{ cursor: 'pointer' }}>prompt sent to model ({prompt.length} chars)</summary>
              <pre style={{ marginTop: 8, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                            background: '#0e0e14', padding: 12, borderRadius: 8, color: '#bcc',
                            lineHeight: 1.5, maxHeight: 480, overflow: 'auto' }}>{prompt}</pre>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}
